import { useChangeCache } from "@/hooks/use-change-cache";
import { useDelayedHover } from "@/hooks/use-delayed-hover";
import { useThresholdSetContext } from "@/registration-tools/common/registration-report/threshold-set-context";
import {
  QualityStatus,
  RegistrationDetails,
} from "@/registration-tools/utils/metrics";
import { useAppStore } from "@/store/store-hooks";
import {
  useClipFrustum,
  useLazyRef,
  useLineRaycast,
} from "@faro-lotv/app-component-toolbox";
import { green, grey, red, yellow } from "@faro-lotv/flat-ui";
import { EMPTY_ARRAY, GUID } from "@faro-lotv/foundation";
import { LineMaterial, VariableLineSegmentsGeometry } from "@faro-lotv/lotv";
import {
  RegistrationEdgeRevision,
  RegistrationEdgeType,
} from "@faro-lotv/service-wires";
import {
  ThreeElements,
  ThreeEvent,
  useFrame,
  useThree,
} from "@react-three/fiber";
import { useCallback, useRef, useState, type JSX, type RefObject } from "react";
import { Color, Mesh, Plane } from "three";
import { LineSegmentsGeometry } from "three-stdlib";
import { Vector3Tuple } from "three/src/Three";
import { useDataPreparationOverlayElements } from "../data-preparation-overlay-context";
import { selectEntityIdToColorMap } from "../store/data-preparation-ui/data-preparation-ui-selectors";
import { selectQualityColorCoding } from "../store/data-preparation-view-options/data-preparation-view-options-selectors";
import { selectDataPreparationTransformCache } from "../store/user-edits/data-preparation-user-edit-selectors";
import { Projection } from "../ui/projection-switch";

/** Size factor for hovered connections.*/
const HOVER_FACTOR = 1.5;

const FALLBACK_POSITION: Vector3Tuple = [0, 0, 0];

// Reused as the output buffer for scratch color conversions,
// to avoid allocating a new array per edge.
const SCRATCH_COLOR_ARRAY: Vector3Tuple = [0, 0, 0];

type RegistrationEdgeRendererProps = Omit<
  ThreeElements["mesh"],
  | "geometry"
  | "material"
  | "visible"
  | "onPointerEnter"
  | "onPointerLeave"
  | "onPointerMove"
> & {
  /** The registration edges to render. */
  registrationEdges: RegistrationEdgeRevision[];

  /** The width to render the edges with. */
  linewidth?: number;

  /** The IDs of the edges that should be highlighted with a thicker line. */
  highlightedEdgeIds: GUID[];

  /** Clipping planes to clip the connection lines with. */
  clippingPlanes?: Plane[];

  /** Called when the mouse enters the edge. */
  onHoverStart?(event: ThreeEvent<PointerEvent>): void;

  /** Called when the mouse leaves the edge. */
  onHoverEnd?(event: ThreeEvent<PointerEvent>): void;

  /** Called when the pointer clicks on the edge. */
  onClick?(event: ThreeEvent<PointerEvent>): void;

  /** Called when the pointer moves over the edge. */
  onPointerMove?(event: ThreeEvent<PointerEvent>): void;
};

/**
 * @returns Draws the given registration edges.
 * Uses instanced rendering for high performance, even with many edges.
 */
export function RegistrationEdgeRenderer({
  registrationEdges,
  onHoverStart,
  onHoverEnd,
  onClick: customOnClick,
  onPointerMove: customPointerMove,
  linewidth,
  highlightedEdgeIds,
  clippingPlanes,
  ...meshProps
}: RegistrationEdgeRendererProps): JSX.Element {
  const clipFrustum = useClipFrustum(clippingPlanes);
  const { mesh, geometry, material } = useRegistrationConnectionsMesh(
    registrationEdges,
    { linewidth, highlightedEdgeIds, clippingPlanes },
  );
  const raycast = useLineRaycast(mesh);

  // Add a delay to hover events to avoid flickering when quickly moving the pointer
  const { onHoverStart: initiateHover, onHoverEnd: endHover } = useDelayedHover<
    ThreeEvent<PointerEvent>
  >({
    onDelayedHoverStart: onHoverStart,
    onDelayedHoverEnd: onHoverEnd,
  });

  const onPointerMove = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      // If a mouse button is pressed, usually for dragging the map or the clipping box, don't
      // trigger the hover event.
      if (event.pointerType === "mouse" && event.nativeEvent.buttons) {
        return;
      }
      // If the pointer is outside an active clipping box, don't trigger the hover event for the
      // cut-off edge.
      if (clipFrustum && !clipFrustum.containsPoint(event.point)) {
        return;
      }

      // Using pointer move to trigger hover start instead of pointer enter
      // This prevents event mismatches when hovering quickly over different instances of the geometry
      initiateHover(event);
      customPointerMove?.(event);
    },
    [clipFrustum, customPointerMove, initiateHover],
  );

  const onClick = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      if (customOnClick) {
        // If the pointer is outside an active clipping box, don't trigger the click event for the
        // cut-off edge.
        if (clipFrustum && !clipFrustum.containsPoint(event.point)) {
          return;
        }

        customOnClick(event);
      }
    },
    [clipFrustum, customOnClick],
  );

  return (
    <mesh
      ref={mesh}
      geometry={geometry}
      material={material}
      visible={registrationEdges.length > 0}
      onClick={onClick}
      onPointerMove={onPointerMove}
      onPointerLeave={endHover}
      {...meshProps}
      // Customize raycast to enable interactions
      // TS can't infer that the property is supported
      // eslint-disable-next-line react/no-unknown-property
      raycast={raycast}
    />
  );
}

type RegistrationConnectionMeshReturn = {
  /** A reference to the mesh to be rendered. */
  mesh: RefObject<Mesh<LineSegmentsGeometry, LineMaterial> | null>;
  /** The line geometry used for the mesh. */
  geometry: LineSegmentsGeometry;
  /** The line material used for the mesh. */
  material: LineMaterial;
};

function useRegistrationConnectionsMesh(
  edges: RegistrationEdgeRevision[],
  {
    linewidth = 5,
    borderWidth = 0.5,
    borderColor = new Color(grey[1300]),
    highlightedEdgeIds = EMPTY_ARRAY,
    clippingPlanes,
  }: Partial<
    Pick<LineMaterial, "linewidth" | "borderWidth" | "borderColor">
  > & {
    highlightedEdgeIds?: GUID[];
    clippingPlanes?: Plane[];
  } = {},
): RegistrationConnectionMeshReturn {
  const { getState } = useAppStore();
  const canvasSize = useThree((state) => state.size);
  const { thresholdSet } = useThresholdSetContext();

  const { projection } = useDataPreparationOverlayElements();

  const [geometry, setGeometry] = useState(
    () => new VariableLineSegmentsGeometry(),
  );

  const [material] = useState(
    () =>
      new LineMaterial({
        // White base color for proper blending with vertex colors
        color: Color.NAMES.white,
        vertexColors: true,
        // Disable tone mapping to keep colors accurate
        toneMapped: false,
        // Transparency needed for anti-aliased lines
        transparent: true,
        depthTest: false,
        depthWrite: false,
        worldUnits: false,
        borderWidth,
        borderColor,
        variableWidth: true,
      }),
  );

  const mesh = useRef<Mesh<VariableLineSegmentsGeometry, LineMaterial>>(null);

  const segmentWidthsRef = useRef<number[]>([]);
  const positionsRef = useRef<number[]>([]);
  const colorsRef = useRef<number[]>([]);
  const idsRef = useRef<GUID[]>([]);

  // Not using a global constant to ensure ThreeJs color management is initialized correctly
  // Otherwise, the colors look different when cold vs. hot reloading the app
  const [colorArrays] = useState(() =>
    Object.freeze({
      good: new Color(green[400]).toArray(),
      medium: new Color(yellow[400]).toArray(),
      poor: new Color(red[400]).toArray(),
      unknown: new Color(grey[700]).toArray(),
    }),
  );

  // Reusable scratch color to avoid allocating a Color per part-of edge.
  // Kept in a ref since it's not used during the React render cycle.
  const scratchColor = useLazyRef(() => new Color());

  const hasChanged = useChangeCache();

  // Update material and geometry before rendering, if needed
  useFrame(() => {
    const state = getState();

    // Use depth testing in 3D mode to reduce visual clutter and improve depth perception
    if (hasChanged("projection", projection)) {
      material.depthTest = projection === Projection.threeDimensional;
      material.needsUpdate = true;
    }

    if (hasChanged("canvasSize", canvasSize)) {
      material.resolution.set(canvasSize.width, canvasSize.height);
      material.needsUpdate = true;
    }
    if (hasChanged("borderWidth", borderWidth)) {
      material.borderWidth = borderWidth;
      material.uniformsNeedUpdate = true;
    }
    if (hasChanged("borderColor", borderColor)) {
      material.borderColor = borderColor;
      material.uniformsNeedUpdate = true;
    }
    if (hasChanged("clippingPlanes", clippingPlanes)) {
      material.clippingPlanes = clippingPlanes ?? [];
      material.needsUpdate = true;
      // The same approach can't be used in CanvasScanMarkers. While the clippingPlanes property
      // exists in MapWaypointsRenderer’s material, it doesn't work.
    }

    const instanceCount = edges.length;

    let curGeometry = geometry;

    // Resize geometry and vertex attributes when needed
    if (hasChanged("instanceCount", instanceCount)) {
      curGeometry = new VariableLineSegmentsGeometry();

      segmentWidthsRef.current.length = instanceCount;
      // 3 coordinates per position, 2 positions per edge
      positionsRef.current.length = instanceCount * 2 * 3;
      // 3 channels per color, 2 colors per edge
      colorsRef.current.length = instanceCount * 2 * 3;
      // 1 ID per edge
      idsRef.current.length = instanceCount;

      setGeometry(curGeometry);
    }

    // Note: geometry changed also implies that the edges changed
    const edgesChanged = hasChanged("edges", edges);

    // Compute which per-edge attributes need updating up front, so all of them
    // can be recomputed in a single pass over `edges` below.
    // Note: `hasChanged` is stateful (see useChangeCache), so the order of these
    // calls and their short-circuiting must be preserved.
    const widthsChanged =
      edgesChanged ||
      hasChanged("highlightedEdgeIds", highlightedEdgeIds) ||
      hasChanged("linewidth", linewidth);

    const transformCache = selectDataPreparationTransformCache(state);

    const positionsChanged =
      edgesChanged || hasChanged("transformCache", transformCache);

    const entityColorMap = selectEntityIdToColorMap(state);
    const showQualityColorCoding = selectQualityColorCoding(state);

    const colorsChanged =
      edgesChanged ||
      hasChanged("showQualityColorCoding", showQualityColorCoding) ||
      hasChanged("thresholdSet", thresholdSet) ||
      hasChanged("colorArrays", colorArrays) ||
      hasChanged("entityColorMap", entityColorMap);

    // Update all per-edge vertex attributes in a single pass. The per-iteration
    // flag checks are constant across the loop, so branch prediction makes them
    // essentially free while avoiding multiple passes over `edges`.
    // Note: `edgesChanged` is omitted here as each flag already includes it.
    if (widthsChanged || positionsChanged || colorsChanged) {
      for (const [idx, edge] of edges.entries()) {
        // Update line width
        if (widthsChanged) {
          segmentWidthsRef.current[idx] = highlightedEdgeIds.includes(edge.id)
            ? linewidth * HOVER_FACTOR
            : linewidth;
        }

        // Update edge position
        if (positionsChanged) {
          const { sourceId, targetId } = edge;

          const sourcePos =
            transformCache[sourceId]?.position ?? FALLBACK_POSITION;
          const targetPos =
            transformCache[targetId]?.position ?? FALLBACK_POSITION;

          positionsRef.current[idx * 6 + 0] = sourcePos[0];
          positionsRef.current[idx * 6 + 1] = sourcePos[1];
          positionsRef.current[idx * 6 + 2] = sourcePos[2];

          positionsRef.current[idx * 6 + 3] = targetPos[0];
          positionsRef.current[idx * 6 + 4] = targetPos[1];
          positionsRef.current[idx * 6 + 5] = targetPos[2];
        }

        // Expose the API edge ID per connection instance, keyed by its instance
        // index. This lets individual connections be identified and inspected
        // (e.g. in E2E tests) by their stable API ID, independent of the order
        // edges happen to be stored in. GUIDs are strings, so they can't live in
        // a numeric buffer attribute and are exposed via `userData` instead (see
        // playwright-three's `getInstanceById`).
        if (edgesChanged) {
          idsRef.current[idx] = edge.id;
        }

        // Update line color
        if (colorsChanged) {
          // Only compute the registration quality when quality color-coding is
          // enabled; otherwise the (allocating) RegistrationDetails is never
          // needed and the color falls back to the scan/unknown color.
          const quality =
            showQualityColorCoding && edge.data?.metrics
              ? new RegistrationDetails(
                  edge.data.metrics.overlap,
                  thresholdSet,
                  edge.data.metrics.rlyHistogram,
                ).registrationQuality
              : QualityStatus.UNKNOWN;

          let edgeColor: readonly number[];

          switch (quality) {
            case QualityStatus.GOOD:
              edgeColor = colorArrays.good;
              break;
            case QualityStatus.MEDIUM:
              edgeColor = colorArrays.medium;
              break;
            case QualityStatus.POOR:
              edgeColor = colorArrays.poor;
              break;
            case QualityStatus.UNKNOWN:
            default:
              // Part-of-edge should match the color of the connected scans
              // assuming both source and target scans share the same color
              if (edge.type === RegistrationEdgeType.partOf) {
                // Reuse a scratch color and array to avoid per-edge allocations
                edgeColor = scratchColor.current
                  .set(entityColorMap[edge.targetId])
                  .toArray(SCRATCH_COLOR_ARRAY);
              } else {
                edgeColor = colorArrays.unknown;
              }
              break;
          }

          colorsRef.current[idx * 6 + 0] = edgeColor[0];
          colorsRef.current[idx * 6 + 1] = edgeColor[1];
          colorsRef.current[idx * 6 + 2] = edgeColor[2];

          colorsRef.current[idx * 6 + 3] = edgeColor[0];
          colorsRef.current[idx * 6 + 4] = edgeColor[1];
          colorsRef.current[idx * 6 + 5] = edgeColor[2];
        }
      }

      if (widthsChanged) {
        curGeometry.setSegmentWidths(segmentWidthsRef.current);
      }
      if (positionsChanged) {
        curGeometry.setPositions(positionsRef.current);
      }
      if (edgesChanged) {
        curGeometry.userData.id = idsRef.current;
      }
      if (colorsChanged) {
        curGeometry.setColors(colorsRef.current);
      }
    }
  });

  return { mesh, geometry, material };
}
